// Mock data for the Instructions / Timely Disclosure screen (route `/instructions`).
// Follows the same data-first pattern used by the performance and comparison screens.

import type {
  InstructionsInfoItem,
  InstructionsPageData,
} from '@app-types/instructions';

// ---- Information list rows -----------------------------------------
const informationSeeds: readonly Omit<
  InstructionsInfoItem,
  'pnac' | 'createdAt'
>[] = [
  {
    key: 'item-1',
    nodeId: '3176:9092',
    title: '三菱UFJ（8306）定款一部変更に関するお知らせ',
    time: '06/04 14:40',
    category: '決算短信',
  },
  {
    key: 'item-2',
    nodeId: '3176:9093',
    title: '三菱UFJ（8306）定款 2024/06/27',
    time: '05/22 18:27',
    category: 'その他',
  },
  {
    key: 'item-3',
    nodeId: '3176:9094',
    title: '三菱UFJ（8306）2026年10月期 第2四半期（中間期）決算短信〔日本基準〕（連結）',
    time: '05/15 12:21',
    category: 'その他',
  },
  {
    key: 'item-4',
    nodeId: '3176:9095',
    title: '三菱UFJ（8306）収益不動産の取得に関するお知らせ',
    time: '05/17 14:20',
    category: '決算短信',
  },
  {
    key: 'item-5',
    nodeId: '3176:9096',
    title: '三菱UFJ（8306）（開示事項の経過）連結子会社間の合併完了に関するお知らせ',
    time: '04/12 07:21',
    category: '業績・配当',
  },
  {
    key: 'item-6',
    nodeId: '3176:9097',
    title: '三菱UFJ（8306）その他の関係会社の異動に関するお知らせ',
    time: '02/02 18:27',
    category: '決算短信',
  },
  {
    key: 'item-7',
    nodeId: '3176:9098',
    title: '三菱UFJ（8306）独立役員届出書',
    time: '01/09 16:32',
    category: '業績・配当',
  },
];

// Figma specifies 7 rows (one page). Pagination needs more rows, so the seeds
// are cycled. Rows past the first page carry a visible suffix so switching
// pages is verifiable, and drop the design node id because they are synthetic
// filler rather than real disclosures.
//
// 4 cycles keep every information tab paginated once category filtering runs
// (per cycle: 決算短信 x3, 業績・配当 x2, その他 x2):
//   all 28 -> 4 pages, 決算短信 12 -> 2, 業績・配当 8 -> 2, その他 8 -> 2.
const INFORMATION_PAGE_COUNT = 4;

const informationItems: InstructionsInfoItem[] = Array.from(
  { length: informationSeeds.length * INFORMATION_PAGE_COUNT },
  (_, index): InstructionsInfoItem => {
    const seed = informationSeeds[index % informationSeeds.length];
    const cycle = Math.floor(index / informationSeeds.length);

    if (cycle === 0) {
      return {
        ...seed,
        key: `item-${index + 1}`,
        pnac: null,
        createdAt: null,
      };
    }

    return {
      ...seed,
      key: `item-${index + 1}`,
      nodeId: undefined,
      title: `${seed.title}（${cycle + 1}）`,
      pnac: null,
      createdAt: null,
    };
  },
);

// ---- Assembled screen data -----------------------------------------
export const mockInstructions: InstructionsPageData = {
  impact: {
    sectionNodeId: '3175:8708',
    headerNodeId: '3175:8709',
    panelNodeId: '3490:21944',
    title: '適時開示影響',
    legend: [
      { key: 'company', label: '--', type: 'line', color: '#2352d6' },
      {
        key: 'disclosure',
        label: '適時開示',
        type: 'event',
        color: '#e60000',
        markerLabel: 'T',
      },
    ],
  },
  information: {
    sectionNodeId: '3175:8698',
    headerNodeId: '3175:8699',
    contentNodeId: '3176:9085',
    title: '適時開示情報一覧',
    tabs: [
      { key: 'all', label: 'すべて', active: true },
      { key: 'earnings', label: '決算短信' },
      { key: 'performanceDividend', label: '業績・配当' },
      { key: 'other', label: 'その他' },
    ],
    items: informationItems,
  },
  // Fallback only: InstructionsPage drives pagination through currentPage /
  // totalPages props. Kept so the component's static mode stays usable.
  pagination: {
    items: ['«', '1', '2', '3', '4', '»'],
    selected: '1',
  },
};

export default mockInstructions;
